import React from 'react';
import { useGetDashboardCategoriesQuery, useGetDashboardProductsQuery, useGetDashboardWarehousesQuery } from 'Services/Admin/DashboardApiSlice';
import { FiChevronDown } from 'react-icons/fi';

// Custom Scrollbar CSS to make it "lite" and mobile-like
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 10px;
    transition: background 0.3s ease;
  }
  .custom-scrollbar:hover::-webkit-scrollbar-thumb {
    background: #E2E8F0;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #CBD5E1;
  }
  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
  .custom-scrollbar:hover {
    scrollbar-color: #E2E8F0 transparent;
  }
`;

// Reusable pill for other filters with custom scrollable dropdown
const FilterPill = ({ icon, label, defaultText, options, hideLabel = false, value, onChange, showResetOption = true, resetOptionLabel }: any) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options?.filter((opt: any) => {
    const displayVal = typeof opt === 'string' ? opt : (opt?.categoryName || opt?.productName || opt?.warehouseName || opt?.name || opt?.itemDesc || opt?.itemName || opt?.title || '');
    return displayVal.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  const selectedDisplay = React.useMemo(() => {
    if (!value) return defaultText;
    const found = options?.find((opt: any) => {
      const val = typeof opt === 'string' ? opt : (opt?.categoryId || opt?.productId || opt?.warehouseId || opt?.id || '');
      return String(val) === String(value);
    });
    if (typeof found === 'string') return found;
    return found?.categoryName || found?.productName || found?.warehouseName || found?.name || found?.itemDesc || found?.itemName || found?.title || defaultText;
  }, [value, options, defaultText]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div 
        ref={dropdownRef}
        className="relative w-full"
      >
        <div 
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center bg-white border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm text-sm hover:shadow-md transition-all cursor-pointer min-w-0 h-full"
          title={selectedDisplay}
        >
          <span className="flex-shrink-0 mr-2 flex items-center justify-center text-lg">{icon}</span>
          {!hideLabel && <span className="text-gray-400 mr-1 whitespace-nowrap flex-shrink-0">{label}</span>}
          <div className="text-[#1B2559] font-bold whitespace-nowrap lg:whitespace-normal pr-5 sm:pr-6 md:pr-8 relative flex-1 min-w-0 text-[11px] sm:text-xs">
            {selectedDisplay}
            <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]">
              <FiChevronDown />
            </span>
          </div>
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] sm:min-w-[280px] bg-white border border-gray-100 shadow-2xl rounded-xl z-[999] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="p-2 border-b border-gray-50">
              <input 
                type="text"
                autoFocus
                placeholder="Search..."
                className="w-full px-3 py-2 text-xs border border-gray-100 rounded-lg outline-none focus:border-blue-200 bg-gray-50/50"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
              {showResetOption && (
                <div 
                  onClick={() => { onChange(''); setIsOpen(false); }}
                  className={`px-4 py-2.5 text-xs cursor-pointer transition-colors ${!value ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {resetOptionLabel || defaultText}
                </div>
              )}
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-400 text-xs italic">No results found</div>
              ) : (
                filteredOptions.map((opt: any, index: number) => {
                  const displayVal = typeof opt === 'string' ? opt : (opt?.categoryName || opt?.productName || opt?.warehouseName || opt?.name || opt?.itemDesc || opt?.itemName || opt?.title || '');
                  const valueVal = typeof opt === 'string' ? opt : (opt?.categoryId || opt?.productId || opt?.warehouseId || opt?.id || displayVal);
                  const isSelected = String(valueVal) === String(value);

                  return (
                    <div 
                      key={index}
                      onClick={() => {
                        onChange(valueVal);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`px-4 py-2.5 text-xs cursor-pointer transition-colors border-l-2 ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-500 text-blue-700 font-bold' 
                          : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-200'
                      }`}
                    >
                      {displayVal}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const DashboardFilters = ({ 
  globalDate, 
  onDateChange, 
  selectedWarehouse, 
  onWarehouseChange,
  selectedChannel,
  onChannelChange,
  selectedCategory,
  onCategoryChange,
  selectedProduct,
  onProductChange
}: any) => {
  const plant = process.env.REACT_APP_PLANT || '';
  
  // Fetch newly integrated endpoints
  const { data: categoryData } = useGetDashboardCategoriesQuery({ plant });
  const { data: productsData } = useGetDashboardProductsQuery({ plant });
  const { data: warehouseData } = useGetDashboardWarehousesQuery({ plant });

  // Safety extraction mapping 
  const categoriesList = Array.isArray(categoryData) ? categoryData : (categoryData?.results || categoryData?.data || []);
  const productsList = Array.isArray(productsData) ? productsData : (productsData?.results || productsData?.data || []);
  const warehouseList = Array.isArray(warehouseData) ? warehouseData : (warehouseData?.results || warehouseData?.data || []);
  const todayStr = new Date().toISOString().split('T')[0];

  const ranges = [
    "Today", "Yesterday", "Last 7 Days", "This Week", "Last 15 Days", 
    "Last 30 Days", "This Month", "Last Month", "This Quarter", 
    "Last Quarter", "This Year", "Last Year", "Custom Range"
  ];

  // Helper to convert DD/MM/YYYY to YYYY-MM-DD for the date input
  const toInputDate = (str: string) => {
    if (!str || !str.includes('/')) return str || '';
    const [d, m, y] = str.split('/');
    return `${y}-${m}-${d}`;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-6 w-full">
      
      {/* 1. NEW GLOBAL DATE RANGE FILTER */}
      <div className={`flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm hover:shadow-md transition-shadow w-full min-h-[42px] ${globalDate.range === 'Custom Range' ? 'col-span-1 sm:col-span-2 xl:col-span-2' : 'col-span-1'}`}>
        <div className="flex items-center px-1 flex-1 min-w-0 h-full">
          <span className="mr-1 text-blue-400 opacity-70 text-base flex-shrink-0">⏱️</span>
          <span className="text-gray-400 mr-1.5 whitespace-nowrap flex-shrink-0 text-[10px] sm:text-[11px]">Date:</span>
          <div className="relative flex-1 min-w-[110px] h-full flex items-center">
            <select 
              value={globalDate.range} 
              onChange={(e) => onDateChange(e.target.value)}
              className={`bg-transparent border-none focus:ring-0 focus:outline-none outline-none text-[#1B2559] font-black cursor-pointer py-0 m-0 pr-6 w-full appearance-none ring-0 shadow-none border-0 ${globalDate.range === 'Custom Range' ? 'text-[10px]' : 'text-[11px] sm:text-[12px] md:text-[12px]'} tracking-tight`}
              style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', boxShadow: 'none' }}
            >
              {ranges.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {/* <span className="absolute right-0.5 top-1/2 -translate-y-1/2 pointer-events-none text-blue-500/30 text-[10px]">
               <FiChevronDown />
            </span> */}
          </div>
        </div>

        {/* Custom Range Date Pickers (Shown only when 'Custom Range' is active) */}
        {globalDate.range === "Custom Range" && (
          <div className="flex items-center gap-2 pl-3 ml-2 border-l border-gray-100 flex-1 min-w-fit py-1">
            <input 
              type="date" 
              max={todayStr}
              value={toInputDate(globalDate.from)}
              onChange={(e) => onDateChange("Custom Range", e.target.value, globalDate.to)}
              className="bg-gray-50 border border-gray-200 text-[#1B2559] text-[11px] font-bold rounded-md px-2 py-0.5 outline-none cursor-pointer w-28"
            />
            <span className="text-gray-400 text-[10px] font-bold">-</span>
            <input 
              type="date" 
              min={toInputDate(globalDate.from)}
              max={todayStr}
              value={toInputDate(globalDate.to)}
              onChange={(e) => onDateChange("Custom Range", globalDate.from, e.target.value)}
              className="bg-gray-50 border border-gray-200 text-[#1B2559] text-[11px] font-bold rounded-md px-2 py-0.5 outline-none cursor-pointer w-28"
            />
          </div>
        )}
      </div>

      {/* Other Static & Dynamic Filters */}
      <FilterPill 
        icon={<span className="text-orange-400">🏢</span>} 
        defaultText="All Warehouses" 
        options={warehouseList}
        hideLabel={true}
        value={selectedWarehouse}
        onChange={onWarehouseChange}
        showResetOption={true}
        resetOptionLabel="All Warehouses"
      />
      
      <FilterPill 
        icon={<span className="text-rose-400">📦</span>} 
        defaultText="All Categories" 
        options={categoriesList} 
        hideLabel={true}
        value={selectedCategory}
        onChange={onCategoryChange}
        showResetOption={true}
        resetOptionLabel="All Categories"
      />
      <FilterPill 
        icon={<span className="text-teal-500">📋</span>} 
        defaultText="All Products" 
        options={productsList} 
        hideLabel={true} 
        value={selectedProduct}
        onChange={onProductChange}
        showResetOption={true}
        resetOptionLabel="All Products"
      />
    </div>
  );
};
